import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import ResourceList, { type ColumnDefinition } from '../components/shared/ResourceList';
import PlanChip from '../components/PlanChip';
import { useStore } from '../store';
import { instanciasAPI } from '../api';
import InstanceCommands from '../components/InstanceCommands';
import SupportAccessDialog from '../components/SupportAccessDialog';

interface PodInfo {
  status: string;
  image: string;
  imageId: string;
  restarts: number;
  podCreated: string | null;
  lastStarted: string | null;
}

interface K8sInfo {
  backend: PodInfo | null;
  frontend: PodInfo | null;
}

type LogLevelFilter = 'all' | 'error' | 'warn' | 'info' | 'http';

interface LogEvent {
  lines: string[];
  text: string;
  level: Exclude<LogLevelFilter, 'all'> | 'default';
}

function IntegradorAvatar({ src, alt, fallback }: { src?: string; alt: string; fallback: string }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(!src);

  useEffect(() => {
    setLoaded(false);
    setFailed(!src);
  }, [src]);

  return (
    <Box sx={{ position: 'relative', width: 38, height: 38 }}>
      {!!src && !loaded && !failed && (
        <Skeleton variant="circular" width={38} height={38} sx={{ position: 'absolute', top: 0, left: 0 }} />
      )}
      <Avatar
        src={!failed ? src : undefined}
        alt={alt}
        sx={{
          width: 38,
          height: 38,
          bgcolor: 'grey.200',
          opacity: loaded || failed ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
        }}
        imgProps={{
          referrerPolicy: 'no-referrer',
          onLoad: () => setLoaded(true),
          onError: () => {
            setFailed(true);
            setLoaded(false);
          },
        }}
      >
        {fallback || 'I'}
      </Avatar>
    </Box>
  );
}

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const formatDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR');
};

const extractDigest = (imageId: string | undefined) => {
  if (!imageId) return '';
  const parts = imageId.split('@sha256:');
  return parts.length > 1 ? parts[1].substring(0, 8) : '';
};

const getStatusColor = (status: string) => {
  const colors: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
    ativo: 'success',
    criando: 'info',
    erro: 'error',
    deletado: 'error',
  };
  return colors[status] || 'default';
};

const getPodStatusColor = (status?: string): 'success' | 'error' | 'warning' | 'default' => {
  if (status === 'Running') return 'success';
  if (status === 'Pending') return 'warning';
  if (status === 'Failed') return 'error';
  return 'default';
};

export default function Instancias() {
  const [supportInstance, setSupportInstance] = useState<{ id: string; name: string } | null>(null);
  const { instancias, integradores, setInstancias } = useStore();
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const response = await instanciasAPI.listar();
        if (active) setInstancias(response.data.data);
      } finally {
        if (active)
          timer = setTimeout(() => {
            void refresh().catch(() => undefined);
          }, 5000);
      }
    };
    void refresh().catch(() => undefined);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [setInstancias]);
  const [k8sInfo, setK8sInfo] = useState<Record<string, K8sInfo>>({});
  const [k8sLoading, setK8sLoading] = useState(false);

  // Logs state
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsInstancia, setLogsInstancia] = useState<{ id: string; nome: string } | null>(null);
  const [logsContainer, setLogsContainer] = useState('backend');
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPeriod, setLogsPeriod] = useState('tail');
  const [logsSearch, setLogsSearch] = useState('');
  const [logsLevel, setLogsLevel] = useState<LogLevelFilter>('all');
  const logsBoxRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const carregarK8sInfo = useCallback(async () => {
    setK8sLoading(true);
    try {
      const res = await instanciasAPI.k8sInfo();
      setK8sInfo(res.data);
    } catch {
      /* silently fail */
    } finally {
      setK8sLoading(false);
    }
  }, []);

  useEffect(() => {
    if (instancias.length > 0) carregarK8sInfo();
  }, [instancias.length, carregarK8sInfo]);

  const fetchLogs = useCallback(async (id: string, container: string, period: string) => {
    setLogsLoading(true);
    try {
      const wasAtBottom = shouldStickToBottomRef.current;
      const sinceSeconds = period !== 'tail' ? Number(period) : undefined;
      const res = await instanciasAPI.logs(id, container, 300, sinceSeconds);
      setLogsContent(res.data.logs);
      if (wasAtBottom) {
        setTimeout(() => logsEndRef.current?.scrollIntoView({ block: 'end' }), 40);
      }
    } catch {
      setLogsContent('[Erro ao buscar logs]');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const handleOpenLogs = async (instanciaId: string, nome: string) => {
    setLogsInstancia({ id: instanciaId, nome });
    setLogsContainer('backend');
    setLogsPeriod('tail');
    setLogsSearch('');
    setLogsLevel('all');
    setLogsOpen(true);
    await fetchLogs(instanciaId, 'backend', 'tail');
  };

  const handleRefreshLogs = () => {
    if (logsInstancia) fetchLogs(logsInstancia.id, logsContainer, logsPeriod);
  };

  const handlePeriodChange = (_: any, newPeriod: string | null) => {
    if (!newPeriod || !logsInstancia) return;
    setLogsPeriod(newPeriod);
    fetchLogs(logsInstancia.id, logsContainer, newPeriod);
  };

  const handleContainerChange = (_: any, newContainer: string | null) => {
    if (!newContainer || !logsInstancia) return;
    setLogsContainer(newContainer);
    fetchLogs(logsInstancia.id, newContainer, logsPeriod);
  };

  useEffect(() => {
    if (!logsOpen || !logsInstancia) {
      return;
    }

    const intervalId = window.setInterval(() => {
      fetchLogs(logsInstancia.id, logsContainer, logsPeriod);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [fetchLogs, logsContainer, logsInstancia, logsOpen, logsPeriod]);

  const isTimestampLine = useCallback((line: string) => {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(line);
  }, []);

  const classifyLogLine = useCallback((line: string): Exclude<LogLevelFilter, 'all'> | 'default' => {
    if (/\b(error|err|fatal|critical|exception)\b/i.test(line)) return 'error';
    if (/\b(warn|warning)\b/i.test(line)) return 'warn';
    if (/\b(get|post|put|patch|delete)\b\s+\//i.test(line)) return 'http';
    if (/\b(info|debug|trace)\b/i.test(line)) return 'info';
    return 'default';
  }, []);

  const processedLogs = useMemo<LogEvent[]>(() => {
    const lines = logsContent ? logsContent.split('\n') : [];
    const query = logsSearch.trim().toLowerCase();
    const events: LogEvent[] = [];

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }

      if (events.length === 0 || isTimestampLine(line)) {
        events.push({
          lines: [line],
          text: line,
          level: classifyLogLine(line),
        });
        continue;
      }

      const currentEvent = events[events.length - 1];
      currentEvent.lines.push(line);
      currentEvent.text = `${currentEvent.text}\n${line}`;

      if (currentEvent.level === 'default') {
        currentEvent.level = classifyLogLine(line);
      }
    }

    return events.filter((event) => {
      if (logsLevel !== 'all' && event.level !== logsLevel) return false;
      if (!query) return true;
      return event.text.toLowerCase().includes(query);
    });
  }, [classifyLogLine, isTimestampLine, logsContent, logsLevel, logsSearch]);

  const getLogLineColor = useCallback((level: Exclude<LogLevelFilter, 'all'> | 'default') => {
    if (level === 'error') return '#ff8a80';
    if (level === 'warn') return '#ffd180';
    if (level === 'info') return '#b39ddb';
    if (level === 'http') return '#80cbc4';
    return '#d4d4d4';
  }, []);

  const handleCopyAlias = useCallback(async (alias: string) => {
    try {
      await navigator.clipboard.writeText(alias);
    } catch {
      // Ignore clipboard failures silently to avoid blocking UI actions.
    }
  }, []);

  const instanciasFiltradas = useMemo(() => {
    return instancias
      .slice()
      .sort((a, b) => new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime());
  }, [instancias]);

  const fetchData = useCallback(
    async ({ page, limit, search }: { page: number; limit: number; search?: string }) => {
      const query = search ? normalizeText(search) : '';

      const filtered = instanciasFiltradas.filter((inst) => {
        const integrador = integradores.find((item) => item._id === inst.integrador_id);
        const dominio = `${integrador?.alias || 'sem-alias'}.postsales.jbesolucoes.com.br`;
        const haystack = normalizeText(
          [
            inst.nome,
            inst.status,
            inst.namespace_k8s || '',
            inst.company_trade_name || '',
            inst.company_business_name || '',
            integrador?.nome || '',
            integrador?.empresa || '',
            integrador?.alias || '',
            dominio,
          ].join(' ')
        );

        return !query || haystack.includes(query);
      });

      const total = filtered.length;
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { total, data };
    },
    [integradores, instanciasFiltradas]
  );

  const columns = useMemo<ColumnDefinition<(typeof instancias)[number]>[]>(() => {
    return [
      {
        header: 'Nome / Empresa',
        accessor: (inst) => {
          const integrador = integradores.find((item) => item._id === inst.integrador_id);
          const nomeExibicao = integrador?.nome || inst.nome;
          const empresaExibicao =
            integrador?.empresa || inst.company_business_name || 'Empresa não informada';
          const aliasExibicao = integrador?.alias || 'sem-alias';
          const planoExibicao = (integrador?.plano || 'free') as 'free' | 'pro' | 'enterprise';
          const logoExibicao = inst.company_branding_logo_url || integrador?.logo_url;
          const initials = nomeExibicao
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() || '')
            .join('');

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <IntegradorAvatar src={logoExibicao || undefined} alt={nomeExibicao} fallback={initials} />
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {nomeExibicao}
                  </Typography>
                  <PlanChip plan={planoExibicao} commercialName={integrador?.commercialPlan?.name} />
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {empresaExibicao}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.4, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }}>
                    {aliasExibicao}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        },
      },
      {
        header: 'Status',
        align: 'center',
        accessor: (inst) => (
          <Box sx={{ maxWidth: 280 }}>
            <Chip label={inst.status} color={getStatusColor(inst.status)} size="small" />
            {inst.logs && (
              <Typography variant="caption" component="p" sx={{ mt: 0.5, whiteSpace: 'normal' }}>
                {inst.logs}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        header: 'Pods K8s',
        accessor: (inst) => {
          const ns = inst.namespace_k8s;
          const info = ns ? k8sInfo[ns] : undefined;

          return info ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <Chip
                label={`B: ${info.backend?.status || '?'}`}
                color={getPodStatusColor(info.backend?.status)}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`F: ${info.frontend?.status || '?'}`}
                color={getPodStatusColor(info.frontend?.status)}
                size="small"
                variant="outlined"
              />
            </Box>
          ) : (
            <Typography variant="caption" color="textSecondary">
              {k8sLoading ? '...' : '—'}
            </Typography>
          );
        },
      },
      {
        header: 'Imagem (digest)',
        accessor: (inst) => {
          const ns = inst.namespace_k8s;
          const info = ns ? k8sInfo[ns] : undefined;

          return (
            <Box>
              {info?.backend ? (
                <Tooltip title={info.backend.image}>
                  <Typography variant="caption" fontFamily="monospace" sx={{ display: 'block' }}>
                    B: {extractDigest(info.backend.imageId) || 'N/A'}
                  </Typography>
                </Tooltip>
              ) : null}
              {info?.frontend ? (
                <Tooltip title={info.frontend.image}>
                  <Typography variant="caption" fontFamily="monospace" sx={{ display: 'block' }}>
                    F: {extractDigest(info.frontend.imageId) || 'N/A'}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },
      {
        header: 'Pod Criado',
        accessor: (inst) => {
          const ns = inst.namespace_k8s;
          const info = ns ? k8sInfo[ns] : undefined;

          return (
            <Box>
              <Typography variant="caption" sx={{ display: 'block' }}>
                B: {formatDate(info?.backend?.podCreated)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                F: {formatDate(info?.frontend?.podCreated)}
              </Typography>
            </Box>
          );
        },
      },
      {
        header: 'Restarts',
        accessor: (inst) => {
          const ns = inst.namespace_k8s;
          const info = ns ? k8sInfo[ns] : undefined;

          return info?.backend ? (
            <Typography variant="caption" sx={{ display: 'block' }}>
              B: {info.backend.restarts} | F: {info.frontend?.restarts ?? '—'}
            </Typography>
          ) : (
            '—'
          );
        },
      },
      {
        header: 'A\u00e7\u00f5es',
        align: 'right',
        accessor: (inst) => (
          <InstanceCommands
            instance={inst}
            onLogs={() => void handleOpenLogs(inst._id, inst.nome)}
            onCopy={() =>
              handleCopyAlias(
                integradores.find((item) => item._id === inst.integrador_id)?.alias ||
                  (inst.namespace_k8s || '').replace(/^solar-/, '')
              )
            }
            onSupport={() => setSupportInstance({ id: inst._id, name: inst.nome })}
            onCompleted={() => {
              void carregarK8sInfo();
            }}
          />
        ),
      },
    ];
  }, [handleCopyAlias, handleOpenLogs, integradores, k8sInfo, k8sLoading, carregarK8sInfo]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography color="text.secondary">
        Instâncias: acesso, logs e operação da aplicação. Dados e configurações da empresa ficam em
        Integradores; contratos ficam em Planos e módulos.
      </Typography>
      <ResourceList
        resourceName="Instâncias"
        searchPlaceholder="Buscar instâncias..."
        emptyMessage="Nenhuma instância criada"
        fetchData={fetchData}
        columns={columns}
        defaultRowsPerPage={10}
      />

      {/* Dialog de Logs */}
      <SupportAccessDialog instance={supportInstance} onClose={() => setSupportInstance(null)} />
      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Logs: {logsInstancia?.nome}
          <IconButton onClick={handleRefreshLogs} disabled={logsLoading} size="small">
            <RefreshIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="textSecondary">
              Container:
            </Typography>
            <ToggleButtonGroup value={logsContainer} exclusive onChange={handleContainerChange} size="small">
              <ToggleButton value="backend">Backend</ToggleButton>
              <ToggleButton value="frontend">Frontend</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="body2" color="textSecondary">
              Período:
            </Typography>
            <ToggleButtonGroup value={logsPeriod} exclusive onChange={handlePeriodChange} size="small">
              <ToggleButton value="tail">Últimas 300 linhas</ToggleButton>
              <ToggleButton value="900">15 min</ToggleButton>
              <ToggleButton value="3600">1 hora</ToggleButton>
              <ToggleButton value="21600">6 horas</ToggleButton>
              <ToggleButton value="86400">24 horas</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="body2" color="textSecondary">
              Tipo:
            </Typography>
            <ToggleButtonGroup
              value={logsLevel}
              exclusive
              onChange={(_, newValue: LogLevelFilter | null) => {
                if (newValue) setLogsLevel(newValue);
              }}
              size="small"
            >
              <ToggleButton value="all">Todos</ToggleButton>
              <ToggleButton value="http">HTTP</ToggleButton>
              <ToggleButton value="error">Erro</ToggleButton>
              <ToggleButton value="warn">Warn</ToggleButton>
              <ToggleButton value="info">Info</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              label="Filtrar logs"
              value={logsSearch}
              onChange={(event) => setLogsSearch(event.target.value)}
              sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>
          {logsLoading && !logsContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              ref={logsBoxRef}
              onScroll={() => {
                const el = logsBoxRef.current;
                if (!el) return;
                const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                shouldStickToBottomRef.current = distanceToBottom < 24;
              }}
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: 'monospace',
                fontSize: 12,
                p: 2,
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {processedLogs.length === 0
                ? '[Sem logs para os filtros selecionados]'
                : processedLogs.map((event, index) => (
                    <Box key={`${index}-${event.lines[0]?.slice(0, 24) || 'log-event'}`} sx={{ mb: 1.5 }}>
                      {event.lines.map((line, lineIndex) => (
                        <Box key={`${index}-${lineIndex}`} sx={{ color: getLogLineColor(event.level) }}>
                          {line}
                        </Box>
                      ))}
                    </Box>
                  ))}
              <div ref={logsEndRef} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
